import { QuestionChallenge } from './QuestionChallenge'
import { ObservationChallenge } from './ObservationChallenge'
import { QRScanner } from './QRScanner'
import { PhotoCapture } from './PhotoCapture'
import { Card } from '../common/Card'
import type { Challenge, ChallengeAnswer } from '../../types'

interface ChallengeModalProps {
  challenge: Challenge
  onSubmit: (answer: ChallengeAnswer) => void
  onClose: () => void
  isSubmitting: boolean
  error?: string
  debugMode?: boolean
}

const challengeIcons = {
  question: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  observation: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  ),
  qr_scan: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
      />
    </svg>
  ),
  photo: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
}

const challengeTitles = {
  question: 'Answer the Question',
  observation: 'What Do You See?',
  qr_scan: 'Scan QR Code',
  photo: 'Take a Photo',
}

export function ChallengeModal({
  challenge,
  onSubmit,
  onClose,
  isSubmitting,
  error,
  debugMode,
}: ChallengeModalProps) {
  const renderChallenge = () => {
    switch (challenge.type) {
      case 'question':
        return (
          <QuestionChallenge
            challenge={challenge}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            debugMode={debugMode}
          />
        )
      case 'observation':
        return (
          <ObservationChallenge
            challenge={challenge}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            debugMode={debugMode}
          />
        )
      case 'qr_scan':
        return (
          <QRScanner
            challenge={challenge}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            debugMode={debugMode}
          />
        )
      case 'photo':
        return (
          <PhotoCapture
            challenge={challenge}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
          />
        )
      default:
        return (
          <QuestionChallenge
            challenge={challenge}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            debugMode={debugMode}
          />
        )
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50 animate-fade-in">
      <div
        className="bg-white w-full rounded-t-3xl max-h-[90vh] overflow-y-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-cream-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
              {challengeIcons[challenge.type]}
            </div>
            <h2 className="text-lg font-bold text-brown-900">
              {challengeTitles[challenge.type]}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-cream-100 flex items-center justify-center text-brown-500"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderChallenge()}

          {error && (
            <Card variant="parchment" padding="md" className="mt-4 border-error-200 bg-error-50">
              <p className="text-error-700 text-sm">{error}</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export { QuestionChallenge, ObservationChallenge, QRScanner, PhotoCapture }
